import { addYears, startOfWeek, subWeeks, subYears } from "date-fns"

import { useGetCollections } from "~/hooks/useCollections"
import useShowHelix from "~/hooks/useShowHelix"
import { api } from "~/utils/api"
import { DateFormatType, formatDate, getYearUTC, startOfTodayUTC } from "~/utils/datetime"
import { selectedForecastWeekRange } from "~/utils/graphUtils"
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "../reusable/Card"
import SuperChart, { type DateChartSeries, type PlotBand } from "../reusable/graphs/SuperChart"
import AreaGraphSilhouette from "../reusable/skeletonSilhouette/AreaGraphSilhouette"
import { formatForecast, formatNumberWithSuffix, roundForecast } from "~/utils/formatting"
import { useSessionStorage } from "usehooks-ts"
import { Switch } from "../reusable/Switch"
import { useEffect, useMemo, useState } from "react"
import { Separator } from "../reusable/Separator"
import { Button, IconButton } from "../reusable/Button"
import { useFilters } from "~/hooks/useFilters"
import { useForecastModels } from "~/hooks/useForecasts"
import { DebouncedInput } from "../reusable/Input"
import { getTailwindValue } from "~/utils/tailwind"
import { ChartType, DateType, LegendAlignment, TodayPlotLine } from "~/constants/graphs"
import { useChartSeriesConfig, useYearColor } from "~/constants/colors"
import { Link, useLocation } from "@tanstack/react-router"
import { formatName } from "~/utils/namingUtils"
import { useForecastGraph } from "../../hooks/useForecastGraph"
import { useVariantsByIds } from "~/hooks/useVariants"
import { useYearComparisonGraph } from "../../hooks/useYearComparisonGraph"
import { useUser } from "~/providers/userProvider"
import { type SelectEventObject } from "highcharts"
import { trackEvent } from "~/hooks/useTrackingService"
import { type DateRange } from "~/stores/dateRangeStore"
import { ManageAdjustmentDialog } from "~/components/forecast/dialogs/ManageAdjustmentDialog"
import ExplainForecastPopoverV2 from "~/components/reusable/ExplainForecastPopoverV2"
import { getCollectionPlotBands } from "~/components/reusable/graphs/utils/GraphPlotLineUtils"
import Select, { type ReactSelectOption } from "../reusable/Select"
import { ArrowSquareOut, FadersHorizontal, Info, Warning } from "@phosphor-icons/react"
import {
  DialogBodyV2,
  DialogContentV2,
  DialogHeaderV2,
  DialogRoot,
  DialogTitle,
} from "~/components/reusable/Dialog"
import { Popover, PopoverContent, PopoverTrigger } from "../reusable/Popover"
import DateRangePicker from "../reusable/DateRangePicker"
import { type QuickSelectFilter } from "~/constants/dateRangeQucikSelectOptions"
import { TabsRoot, TabsList, TabsTrigger } from "../reusable/Tabs"
import { InvalidPreOrdersModal } from "~/components/home/InvalidPreOrdersModal"
import { Tooltip } from "~/components/reusable/Tooltip"
import { addMonths, subMonths } from "date-fns"
import { buildPreorderSeries, dateSeries } from "~/utils/graphUtils"

// Stable default dates - computed once on module load to avoid re-creating Date objects on every render
const DEFAULT_START_DATE = subYears(startOfTodayUTC(), 1)
const DEFAULT_END_DATE = addYears(startOfTodayUTC(), 1)

const PERIOD_QUICK_SELECTS: QuickSelectFilter[] = [
  {
    label: "Last 3 months + next 3 months",
    resolve: () => ({ from: subMonths(startOfTodayUTC(), 3), to: addMonths(startOfTodayUTC(), 3) }),
  },
  {
    label: "Last 6 months + next 6 months",
    resolve: () => ({ from: subMonths(startOfTodayUTC(), 6), to: addMonths(startOfTodayUTC(), 6) }),
  },
  {
    label: "Last 12 months + next 12 months",
    resolve: () => ({
      from: subMonths(startOfTodayUTC(), 12),
      to: addMonths(startOfTodayUTC(), 12),
    }),
  },
  {
    label: "Last 24 months + next 12 months",
    resolve: () => ({
      from: subMonths(startOfTodayUTC(), 24),
      to: addMonths(startOfTodayUTC(), 12),
    }),
  },
  {
    label: "Last 36 months + next 12 months",
    resolve: () => ({
      from: subMonths(startOfTodayUTC(), 36),
      to: addMonths(startOfTodayUTC(), 12),
    }),
  },
]

interface SalesAndForecastGraphCardProps {
  orgId?: string
  variantIds: string[]
  warehouseId?: string // Note: Currently not used as useForecastGraph uses the warehouse from the store
  channel?: string
  graphHeight?: number
  startDate?: Date
  endDate?: Date
  customXPlotBands?: PlotBand[]
  linkToForecast?: boolean
  isMonthly?: boolean
  graphOnly?: boolean
  todayPlotLine?: TodayPlotLine
  showPreorders?: boolean
  canToggleMonthly?: boolean
  netSalesMode?: "off" | "both" | "only"
  isNoos?: boolean
  skuId?: string
  /** Show an expand button that opens the graph in a larger dialog. Opt-in (default false). */
  expandable?: boolean
}

export const SalesAndForecastGraphCard = ({
  variantIds,
  warehouseId,
  channel,
  graphHeight = 280,
  startDate = DEFAULT_START_DATE,
  endDate = DEFAULT_END_DATE,
  customXPlotBands,
  linkToForecast = false,
  isMonthly = false,
  graphOnly = false,
  todayPlotLine = TodayPlotLine.Basic,
  showPreorders = false,
  canToggleMonthly = true,
  netSalesMode = "off",
  isNoos = false,
  skuId,
  expandable = false,
}: SalesAndForecastGraphCardProps) => {
  const [showPreordersToggled, setShowPreordersToggled] = useSessionStorage(
    "showPreorders",
    showPreorders,
  )
  const { data: hasPreOrders } = api.preOrder.hasPreOrders.useQuery()
  const { permissions } = useUser()
  const { pathname } = useLocation()
  const showHelix = useShowHelix()

  const [selectedGraphDateRange, setSelectedGraphDateRange] = useState<DateRange>()
  const [openAdjustmentDialog, setOpenAdjustmentDialog] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)
  const [storedIsMonthly, setStoredIsMonthly] = useSessionStorage(
    `isMonthly:${pathname}`,
    isMonthly,
  )
  const effectiveIsMonthly = canToggleMonthly ? storedIsMonthly : isMonthly

  // Year comparison state - current year is always the baseline, select only stores comparison years
  const currentYear = getYearUTC(startOfTodayUTC())
  const [comparisonYears, setComparisonYears] = useSessionStorage<number[]>(
    `yearComparisonYears:${pathname}`,
    [],
  )
  const yearComparisonEnabled = comparisonYears.length > 0
  // All years to display: current year + any comparison years
  const allSelectedYears = useMemo(
    () => [currentYear, ...comparisonYears].sort((a, b) => b - a),
    [currentYear, comparisonYears],
  )

  // Date range override state
  const [overrideStartDate, setOverrideStartDate] = useSessionStorage<string | null>(
    `graphStartDate:${pathname}`,
    null,
  )
  const [overrideEndDate, setOverrideEndDate] = useSessionStorage<string | null>(
    `graphEndDate:${pathname}`,
    null,
  )
  const parsedOverrideStart = useMemo(
    () => (overrideStartDate ? new Date(overrideStartDate) : undefined),
    [overrideStartDate],
  )
  const parsedOverrideEnd = useMemo(
    () => (overrideEndDate ? new Date(overrideEndDate) : undefined),
    [overrideEndDate],
  )

  // When year comparison is enabled, fetch full year data for current year
  const effectiveStartDate = yearComparisonEnabled
    ? DEFAULT_START_DATE
    : (parsedOverrideStart ?? startDate)
  const effectiveEndDate = yearComparisonEnabled ? DEFAULT_END_DATE : (parsedOverrideEnd ?? endDate)

  const {
    salesGraphData,
    netSalesGraphData,
    adjustedAndForecastIsSame,
    hasDemandPlanForecast,
    adjustedForecastGraphData,
    rawForecastGraphData,
    knownPreOrdersGraphData,
    invalidPreOrdersGraphData,
    unfulfillablePreOrdersGraphData,
    hasUnfulfillablePreOrders,
    atRiskPreOrdersGraphData,
    hasAtRiskPreOrders,
    hasInvalidPreOrders,
    invalidVariantCount,
    forecastWithoutPreOrdersGraphData,
    isLoading,
  } = useForecastGraph({
    variantIds,
    warehouseId,
    monthly: effectiveIsMonthly,
    startDate: effectiveStartDate,
    endDate: effectiveEndDate,
    channel,
    skuId,
  })

  // Year comparison data - only fetch comparison years (current year uses default styling)
  const { yearSeriesData, availableYears } = useYearComparisonGraph({
    variantIds,
    warehouseId,
    channel,
    selectedYears: comparisonYears,
    referenceYear: currentYear,
    enabled: yearComparisonEnabled,
    monthly: effectiveIsMonthly,
  })

  // Year options for the select - exclude current year (it's always the baseline)
  const yearOptions: ReactSelectOption<number>[] = useMemo(
    () =>
      availableYears
        .filter((year) => year !== currentYear)
        .map((year) => ({ label: String(year), value: year })),
    [availableYears, currentYear],
  )

  const selectedYearOptions = useMemo(
    () => yearOptions.filter((opt) => comparisonYears.includes(opt.value)),
    [yearOptions, comparisonYears],
  )

  const handleYearSelectionChange = (newValue: readonly ReactSelectOption<number>[] | null) => {
    const years = newValue ? newValue.map((opt) => opt.value).sort((a, b) => b - a) : []
    setComparisonYears(years)
    if (years.length > 0) setShowPreordersToggled(false)
  }

  const [showInvalidModal, setShowInvalidModal] = useState(false)

  const { data: collections } = api.collection.getCollectionsForVariants.useQuery(
    { variantIds: [variantIds[0]!] },
    { enabled: variantIds.length === 1 },
  )

  const xPlotBands = useMemo(() => {
    const collectionPlotBands = getCollectionPlotBands(collections ?? [])
    return customXPlotBands ? [...collectionPlotBands, ...customXPlotBands] : collectionPlotBands
  }, [collections, customXPlotBands])

  useEffect(() => {
    setShowPreordersToggled(showPreorders)
  }, [showPreorders])

  const chartData = useMemo(() => {
    const baseSeries =
      netSalesMode === "only"
        ? [netSalesGraphData]
        : netSalesMode === "both"
          ? [salesGraphData, netSalesGraphData]
          : [salesGraphData]

    const hideRawDemand = hasDemandPlanForecast || showHelix
    const forecastSeries = [
      ...(adjustedAndForecastIsSame && !hideRawDemand ? [] : [adjustedForecastGraphData]),
      ...(hideRawDemand ? [] : [rawForecastGraphData]),
      ...(hasPreOrders
        ? [
            {
              ...forecastWithoutPreOrdersGraphData,
              hidden: true,
            },
          ]
        : []),
    ]

    let result: DateChartSeries[]
    if (showPreordersToggled) {
      const preorderSeries = buildPreorderSeries({
        hasPreOrders,
        hasAtRiskPreOrders,
        hasUnfulfillablePreOrders,
        knownPreOrdersGraphData,
        atRiskPreOrdersGraphData,
        unfulfillablePreOrdersGraphData,
      })
      result = [
        ...baseSeries,
        ...forecastSeries,
        ...preorderSeries,
        ...(hasInvalidPreOrders ? [invalidPreOrdersGraphData] : []),
      ] as DateChartSeries[]
    } else {
      result = [...baseSeries, ...forecastSeries] as DateChartSeries[]
    }

    // Add comparison year lines when enabled
    if (yearComparisonEnabled && yearSeriesData.length > 0) {
      result = [...result, ...yearSeriesData]
    }

    return result
  }, [
    yearComparisonEnabled,
    yearSeriesData,
    netSalesMode,
    salesGraphData,
    netSalesGraphData,
    adjustedAndForecastIsSame,
    hasDemandPlanForecast,
    showHelix,
    adjustedForecastGraphData,
    forecastWithoutPreOrdersGraphData,
    hasPreOrders,
    rawForecastGraphData,
    showPreordersToggled,
    knownPreOrdersGraphData,
    hasInvalidPreOrders,
    invalidPreOrdersGraphData,
    hasUnfulfillablePreOrders,
    unfulfillablePreOrdersGraphData,
    hasAtRiskPreOrders,
    atRiskPreOrdersGraphData,
  ])

  const showLoading = isLoading

  // In year comparison mode, show full year range aligned to reference year (most recent selected)
  const comparisonReferenceYear = Math.max(...allSelectedYears)
  const comparisonDateRange = useMemo(
    () => ({
      start: new Date(Date.UTC(comparisonReferenceYear, 0, 1)),
      end: new Date(Date.UTC(comparisonReferenceYear, 11, 31)),
    }),
    [comparisonReferenceYear],
  )

  if (showLoading) {
    if (graphOnly) {
      // The silhouette's SVG is ratio-sized, so it reports a ~966px max-content width and would
      // stretch any shrink-to-fit ancestor (the table tooltip) far wider than the loaded graph.
      // width:0 + min-width:100% keeps it full-width while contributing nothing to that sizing.
      return (
        <div style={{ height: graphHeight, width: 0, minWidth: "100%" }}>
          <AreaGraphSilhouette />
        </div>
      )
    }
    return (
      <Card>
        <CardHeader>
          <CardTitle>{formatName("sale", true, true)} & Demand</CardTitle>
        </CardHeader>
        <CardContent style={{ height: graphHeight }}>
          <AreaGraphSilhouette />
        </CardContent>
      </Card>
    )
  }

  function forecastGraphRangeSelected(event: SelectEventObject): boolean {
    const range = selectedForecastWeekRange(event)

    if (range) {
      trackEvent("Product Graph Range Selected")
      setSelectedGraphDateRange(range)
      setOpenAdjustmentDialog(true)
    }

    return false
  }

  const graph = (
    <>
      <SuperChart
        type={ChartType.TimeSeries}
        data={chartData}
        dateType={effectiveIsMonthly ? DateType.Monthly : DateType.Weekly}
        compact={graphHeight < 200}
        height={graphHeight}
        plotBands={yearComparisonEnabled ? undefined : { x: xPlotBands }}
        dateRange={
          yearComparisonEnabled
            ? comparisonDateRange
            : {
                start: effectiveStartDate,
                end: effectiveEndDate,
              }
        }
        todayPlotLine={yearComparisonEnabled ? TodayPlotLine.Hide : todayPlotLine}
        legendAlignment={LegendAlignment.Bottom}
        selectionEvent={
          permissions.canEdit && !yearComparisonEnabled ? forecastGraphRangeSelected : undefined
        }
      />
      {openAdjustmentDialog && (
        <ManageAdjustmentDialog
          variantIds={variantIds ?? []}
          customTrigger={<></>}
          customOpen
          onClose={() => {
            setSelectedGraphDateRange(undefined)
            setOpenAdjustmentDialog(false)
          }}
          presetStartDate={selectedGraphDateRange?.start}
          presetEndDate={selectedGraphDateRange?.end}
        />
      )}
    </>
  )

  if (graphOnly) {
    return graph
  }

  return (
    <>
      <Card className="w-full min-w-0">
        <CardHeader>
          <div className="flex items-center gap-2">
            <CardTitle>{formatName("sale", true, true)} & Demand</CardTitle>
            {isNoos && (
              <Tooltip
                title={`Gross ${formatName("sale", true)} data: ${formatDate(subWeeks(startOfTodayUTC(), 52), DateFormatType.FullDateShort)} – ${formatDate(startOfTodayUTC(), DateFormatType.FullDateShort)}`}
                description={`NOOS collections are limited to the last 52 weeks of gross ${formatName("sale", true)} data.`}
              >
                <Info size={16} className="text-fg-muted cursor-default" />
              </Tooltip>
            )}
          </div>
          <div className="flex items-center gap-2">
            {hasInvalidPreOrders && showPreordersToggled && (
              <Button variant="neutral" size="sm" onClick={() => setShowInvalidModal(true)}>
                <Warning className="!fill-indicator-checkbox-red" weight="bold" />
                Review {invalidVariantCount} {formatName("preorder", true)}
              </Button>
            )}
            {!yearComparisonEnabled && variantIds.length === 1 && (
              <ExplainForecastPopoverV2 variantId={variantIds[0]!} />
            )}
            <Popover>
              <PopoverTrigger asChild>
                <IconButton variant="neutral" size="sm">
                  <FadersHorizontal />
                </IconButton>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-80 flex flex-col gap-3">
                {hasPreOrders && (
                  <div className="flex items-center justify-between">
                    <div className="label-sm">Show {formatName("preorder", true)}</div>
                    <Switch
                      checked={showPreordersToggled}
                      onCheckedChange={setShowPreordersToggled}
                      disabled={yearComparisonEnabled}
                    />
                  </div>
                )}
                {hasPreOrders && <Separator />}
                {canToggleMonthly && (
                  <TabsRoot
                    value={effectiveIsMonthly ? "month" : "week"}
                    onValueChange={(v) => setStoredIsMonthly(v === "month")}
                    className="w-full"
                  >
                    <TabsList size="sm" className="w-full">
                      <TabsTrigger size="sm" value="week" className="flex-1">
                        Weekly
                      </TabsTrigger>
                      <TabsTrigger size="sm" value="month" className="flex-1">
                        Monthly
                      </TabsTrigger>
                    </TabsList>
                  </TabsRoot>
                )}
                {canToggleMonthly && <Separator />}
                <div className="flex flex-col gap-1.5">
                  <div className="paragraph-sm font-medium text-fg-subtle">Period</div>
                  <DateRangePicker
                    startDate={parsedOverrideStart ?? startDate}
                    endDate={parsedOverrideEnd ?? endDate}
                    maxDate={addYears(startOfTodayUTC(), 2)}
                    onStartDateChange={(d) => setOverrideStartDate(d?.toISOString() ?? null)}
                    onEndDateChange={(d) => setOverrideEndDate(d?.toISOString() ?? null)}
                    disabled={yearComparisonEnabled}
                    showDefaultQuickSelects
                    quickSelectOptions={PERIOD_QUICK_SELECTS}
                    clearable
                  />
                </div>
                <Separator />
                <div className="flex flex-col gap-1.5">
                  <div className="paragraph-sm font-medium text-fg-subtle">Compare years</div>
                  <Select
                    isMulti
                    options={yearOptions}
                    value={selectedYearOptions}
                    onChange={handleYearSelectionChange}
                    placeholder="Select years..."
                    size="sm"
                    isSearchable={false}
                  />
                </div>
              </PopoverContent>
            </Popover>
            {expandable && (
              <Tooltip title="Expand graph">
                <IconButton variant="neutral" size="sm" onClick={() => setIsExpanded(true)}>
                  <ArrowSquareOut />
                </IconButton>
              </Tooltip>
            )}
          </div>
        </CardHeader>
        <CardContent className="min-w-0">{graph}</CardContent>
        {linkToForecast && (
          <CardFooter>
            <Link to={showHelix ? "/demand/sales-and-demand" : "/insights/forecast-calendar"}>
              {showHelix ? "View sales & demand" : "View demand calendar"}
            </Link>
          </CardFooter>
        )}
        {showInvalidModal && (
          <InvalidPreOrdersModal
            open={showInvalidModal}
            onClose={() => setShowInvalidModal(false)}
            variantIds={variantIds}
            warehouseId={warehouseId}
            channel={channel}
            startDate={startDate}
            endDate={endDate}
          />
        )}
      </Card>

      {expandable && (
        <DialogRoot open={isExpanded} onOpenChange={setIsExpanded}>
          <DialogContentV2 className="w-[80vw] max-w-[80vw]">
            <DialogHeaderV2>
              <DialogTitle>{formatName("sale", true, true)} & Demand</DialogTitle>
            </DialogHeaderV2>
            <DialogBodyV2 className="max-h-[85vh]">
              <SalesAndForecastGraphCard
                variantIds={variantIds}
                warehouseId={warehouseId}
                channel={channel}
                startDate={startDate}
                endDate={endDate}
                customXPlotBands={customXPlotBands}
                linkToForecast={linkToForecast}
                isMonthly={isMonthly}
                todayPlotLine={todayPlotLine}
                showPreorders={showPreorders}
                canToggleMonthly={canToggleMonthly}
                netSalesMode={netSalesMode}
                isNoos={isNoos}
                skuId={skuId}
                graphHeight={600}
                graphOnly
              />
            </DialogBodyV2>
          </DialogContentV2>
        </DialogRoot>
      )}
    </>
  )
}

export const SalesAndForecastDetailedGraphCard: React.FC<SalesAndForecastGraphCardProps> = ({
  variantIds,
  warehouseId,
  graphHeight = 280,
}) => {
  const chartConfig = useChartSeriesConfig()
  const yearColor = useYearColor()
  const showHelix = useShowHelix()
  const [showCollections, setShowCollections] = useSessionStorage("showCollections", false)
  const [showPreorders, setShowPreorders] = useSessionStorage("showPreorders", false)
  const { filters, setFilters } = useFilters()
  const { data: forecastModels } = useForecastModels()

  const [collectionSearchText, setCollectionSearchText] = useState<string>("")
  const [selectedCollections, setSelectedCollections] = useState<string[]>([])
  const [selectedForecastModel, setSelectedForecastModel] = useState<string>("")

  const [includeHistoricalPreorders, setIncludeHistoricalPreorders] = useSessionStorage(
    "includeHistoricalPreorders",
    true,
  )
  const [includeOngoingSales, setIncludeOngoingSales] = useSessionStorage(
    "includeOngoingSales",
    true,
  )
  const [includeKnownPreorders, setIncludeKnownPreorders] = useSessionStorage(
    "includeKnownPreorders",
    true,
  )
  const [includeForecastedPreorders, setIncludeForecastedPreorders] = useSessionStorage(
    "includeForecastedPreorders",
    true,
  )
  const [includeForecastWithoutPreorders, setIncludeForecastWithoutPreorders] = useSessionStorage(
    "includeForecastWithoutPreorders",
    true,
  )

  const { data: collections } = useGetCollections()
  const { data: hasExternalCollections } = api.collection.hasExternalCollections.useQuery()
  const { data: salesAndForecastData, isLoading: salesAndForecastLoading } =
    api.product.salesAndForecasts.useQuery(
      {
        variantIds,
        warehouseId,
        externalCollectionIds:
          !!collections && selectedCollections.length > 0
            ? selectedCollections.map(
                (collection) => collections?.find((c) => c.name === collection)?.externalId ?? "",
              )
            : undefined,
      },
      {
        enabled: (variantIds && variantIds.length > 0) || !variantIds,
      },
    )

  const { data: salesByCollection, isLoading: salesByCollectionLoading } =
    api.product.salesByExternalCollection.useQuery(
      {
        variantIds,
        externalCollectionIds: selectedCollections.map(
          (collection) => collections?.find((c) => c.name === collection)?.externalId ?? "",
        ),
      },
      {
        enabled:
          hasExternalCollections &&
          variantIds &&
          variantIds.length > 0 &&
          selectedCollections.length > 0,
      },
    )

  const salesSum = useMemo(() => {
    return salesAndForecastData?.sales.reduce((acc, curr) => acc + curr.value, 0) ?? 0
  }, [salesAndForecastData])

  const rawForecastSum = useMemo(() => {
    return salesAndForecastData?.rawForecasts.reduce((acc, curr) => acc + curr.value, 0) ?? 0
  }, [salesAndForecastData])

  const forecastSum = useMemo(() => {
    return salesAndForecastData?.forecasts.reduce((acc, curr) => acc + curr.value, 0) ?? 0
  }, [salesAndForecastData])

  const knownHistoricalPreOrdersSum = useMemo(() => {
    return (
      salesAndForecastData?.preOrders
        .filter((x) => x.date < startOfWeek(startOfTodayUTC()))
        .reduce((acc, curr) => acc + curr.value, 0) ?? 0
    )
  }, [salesAndForecastData])

  const knownPreOrdersSum = useMemo(() => {
    return (
      salesAndForecastData?.preOrders
        .filter((x) => x.date > startOfWeek(startOfTodayUTC()))
        .reduce((acc, curr) => acc + curr.value, 0) ?? 0
    )
  }, [salesAndForecastData])

  const forecastedPreOrdersSum = useMemo(() => {
    return (
      salesAndForecastData?.unknownPreOrders.reduce(
        (acc, curr) => acc + curr.rawPreorderForecast,
        0,
      ) ?? 0
    )
  }, [salesAndForecastData])

  const forecastWithoutPreordersSum = useMemo(() => {
    return rawForecastSum - knownPreOrdersSum - forecastedPreOrdersSum
  }, [rawForecastSum, knownPreOrdersSum, forecastedPreOrdersSum])

  const collectionPlotBands: { x?: PlotBand[] } = useMemo(() => {
    const plotbands: PlotBand[] =
      collections
        ?.map((collection) => [
          {
            text: collection.name,
            from: collection.startDate?.getTime() ?? 0,
            to: collection.endDate?.getTime() ?? 0,
            color: "#8B5CF61A",
            borderColor: getTailwindValue("--color-violet-300"),
            textColor: getTailwindValue("--color-violet-500"),
          },
        ])
        .flatMap((x) => x) ?? []
    return showCollections
      ? {
          x: plotbands.filter((x) => selectedCollections.includes(x.text ?? "")),
        }
      : {}
  }, [collections, showCollections, selectedCollections])

  useEffect(() => {
    if (selectedCollections.length === 0) {
      setFilters(filters?.filter((f) => f.field !== "collections"))
      return
    }
    const collectionFilter = {
      field: "collections",
      operator: "oneOf",
      value: "",
      by: "skuId",
      values: selectedCollections,
    }

    setFilters(
      filters
        ? [...filters.filter((f) => f.field !== "collections"), collectionFilter]
        : [collectionFilter],
    )
  }, [selectedCollections])

  useEffect(() => {
    if (collectionSearchText.length > 0) {
      setSelectedCollections(
        collections
          ?.filter((c) => c.name.toLowerCase().includes(collectionSearchText.toLowerCase()))
          .map((c) => c.name) ?? [],
      )
    }
  }, [collectionSearchText])

  useEffect(() => {
    if (!selectedForecastModel) {
      setFilters(filters?.filter((f) => f.field !== "model"))
      return
    }

    const forecastModelFilter = {
      field: "model",
      operator: "oneOf",
      value: "",
      by: "skuId",
      values: [selectedForecastModel],
    }

    setFilters(
      filters
        ? [...filters.filter((f) => f.field !== "model"), forecastModelFilter]
        : [forecastModelFilter],
    )
  }, [forecastModels, selectedForecastModel])

  const handleForecastModelClick = (model: string) => {
    if (selectedForecastModel === model) {
      setSelectedForecastModel("")
    } else {
      setSelectedForecastModel(model)
    }
  }

  const handleCollectionClick = (collectionName: string) => {
    if (selectedCollections.includes(collectionName)) {
      setSelectedCollections(selectedCollections.filter((c) => c !== collectionName))
    } else {
      setSelectedCollections([...selectedCollections, collectionName])
    }
  }

  const collectionsMap = useMemo(() => {
    return new Map(collections?.map((c) => [c.externalId, c.name]) ?? [])
  }, [collections])

  const salesByCollectionsGraphData: DateChartSeries[] = useMemo(() => {
    return Object.entries(salesByCollection ?? {}).map(([collectionId, sales], index) => ({
      name: collectionsMap.get(collectionId) ?? "",
      data: sales.map(({ date, sales }) => ({ milliseconds: date.getTime(), value: sales })),
      type: "spline",
      colors: [yearColor(index)],
      dashStyle: chartConfig.sales.dashStyle,
    }))
  }, [salesByCollection, collectionsMap, chartConfig, yearColor])

  const knownPreOrdersMap = useMemo(() => {
    return new Map(
      salesAndForecastData?.preOrders.map(({ date, value }) => [date.getTime(), value]) ?? [],
    )
  }, [salesAndForecastData?.preOrders])

  const forecastedPreOrdersMap = useMemo(() => {
    return new Map(
      salesAndForecastData?.unknownPreOrders.map(({ date, rawPreorderForecast }) => [
        date.getTime(),
        roundForecast(rawPreorderForecast),
      ]) ?? [],
    )
  }, [salesAndForecastData?.unknownPreOrders])

  const forecastWithoutPreordersMap = useMemo(() => {
    return new Map(
      salesAndForecastData?.rawForecasts.map(({ date, value }) => [
        date.getTime(),
        value -
          (knownPreOrdersMap.get(date.getTime()) ?? 0) -
          (forecastedPreOrdersMap.get(date.getTime()) ?? 0),
      ]) ?? [],
    )
  }, [salesAndForecastData?.rawForecasts, knownPreOrdersMap, forecastedPreOrdersMap])

  const salesWithoutPreordersMap = useMemo(() => {
    return new Map(
      salesAndForecastData?.sales.map(({ date, value }) => [
        date.getTime(),
        Math.round(value - (knownPreOrdersMap.get(date.getTime()) ?? 0)),
      ]) ?? [],
    )
  }, [salesAndForecastData?.sales, knownPreOrdersMap])

  const detailedVariants = useVariantsByIds(variantIds)
  const isMixedNoosAndFashionDetailed =
    detailedVariants.some((v) => v.productType === "noos") &&
    detailedVariants.some((v) => v.productType === "fashion")

  if (salesAndForecastLoading || salesByCollectionLoading) {
    return <AreaGraphSilhouette />
  }

  const hasDemandPlanForecastDetailed = salesAndForecastData?.hasDemandPlanForecast ?? false
  const hideRawDemand = hasDemandPlanForecastDetailed || showHelix

  const forecastGraphData: DateChartSeries = dateSeries(chartConfig.demand, {
    name: hasDemandPlanForecastDetailed
      ? isMixedNoosAndFashionDetailed
        ? "Demand"
        : "Demand Plan"
      : "Demand",
    type: "spline",
    data: salesAndForecastData
      ? salesAndForecastData["forecasts"].map(({ date, value }) => ({
          milliseconds: date.getTime(),
          value: roundForecast(value),
        }))
      : [],
  })

  const knownPreOrders: DateChartSeries = dateSeries(chartConfig.preOrder, {
    name: `Known ${formatName("preorder", true)}`,
    type: "column",
    data:
      salesAndForecastData?.preOrders.map(({ date, value }) => ({
        milliseconds: date.getTime(),
        value,
      })) ?? [],
  })

  const rawForecastGraphData: DateChartSeries = dateSeries(chartConfig.rawDemand, {
    name: "Raw Demand",
    type: "spline",
    data: salesAndForecastData
      ? salesAndForecastData["rawForecasts"].map(({ date, value }) => ({
          milliseconds: date.getTime(),
          value:
            value -
            (!includeKnownPreorders ? (knownPreOrdersMap.get(date.getTime()) ?? 0) : 0) -
            (!includeForecastedPreorders ? (forecastedPreOrdersMap.get(date.getTime()) ?? 0) : 0) -
            (!includeForecastWithoutPreorders
              ? (forecastWithoutPreordersMap.get(date.getTime()) ?? 0)
              : 0),
        }))
      : [],
  })

  const salesGraphData: DateChartSeries = dateSeries(chartConfig.sales, {
    name: formatName("sale", true, true),
    type: "areaspline",
    data: salesAndForecastData
      ? salesAndForecastData["sales"].map(({ date, value }) => ({
          milliseconds: date.getTime(),
          value: Math.round(
            value -
              (!includeHistoricalPreorders ? (knownPreOrdersMap.get(date.getTime()) ?? 0) : 0) -
              (!includeOngoingSales ? (salesWithoutPreordersMap.get(date.getTime()) ?? 0) : 0),
          ),
        }))
      : [],
  })

  return (
    <Card className="w-full h-fit">
      <CardHeader>
        <CardTitle>{formatName("sale", true, true)} & Demand</CardTitle>
        <div className="flex items-center gap-1">
          <div className="text-sm text-subtle-500">Show collections</div>
          <Switch checked={showCollections} onCheckedChange={setShowCollections} />
          <div className="text-sm text-subtle-500">Show {formatName("preorder", true)}</div>
          <Switch checked={showPreorders} onCheckedChange={setShowPreorders} />
        </div>
      </CardHeader>
      <CardContent className="flex flex-row gap-4 w-full">
        <div className="flex flex-col gap-2 w-full">
          <SuperChart
            type={ChartType.TimeSeries}
            plotBands={collectionPlotBands}
            data={
              showPreorders
                ? [
                    salesGraphData,
                    forecastGraphData,
                    ...(hideRawDemand ? [] : [rawForecastGraphData]),
                    knownPreOrders,
                    ...salesByCollectionsGraphData,
                  ]
                : [
                    salesGraphData,
                    forecastGraphData,
                    ...(hideRawDemand ? [] : [rawForecastGraphData]),
                  ]
            }
            compact={graphHeight < 200}
            height={graphHeight}
            dateRange={{
              start: subYears(startOfTodayUTC(), 1),
              end: addYears(startOfTodayUTC(), 1),
            }}
          />

          <div className="flex flex-col gap-2">
            <div className="text-fg-subtle text-md font-medium">Forecast model</div>
            <div className="flex flex-wrap gap-2">
              {forecastModels?.map((model) => (
                <Button
                  size="sm"
                  key={model}
                  variant={selectedForecastModel === model ? "primary" : "neutral"}
                  onClick={() => handleForecastModelClick(model)}
                >
                  {model}
                </Button>
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <div className="text-fg-subtle text-md font-medium">Collections filters</div>
            <div className="flex flex-row gap-2 items-center">
              <DebouncedInput
                placeholder="Search collections"
                defaultValue={collectionSearchText}
                small
                onChange={(e) => setCollectionSearchText(e.target.value)}
                debounceTime={1000}
              />
              <Button
                size="sm"
                variant="neutral"
                onClick={() => {
                  setCollectionSearchText("")
                  setSelectedCollections([])
                }}
              >
                Clear selection
              </Button>
            </div>
            <Separator />
            <div className="flex flex-wrap gap-2">
              {collections?.map((collection) => (
                <Button
                  size="sm"
                  key={collection.id}
                  variant={selectedCollections.includes(collection.name) ? "primary" : "neutral"}
                  onClick={() => handleCollectionClick(collection.name)}
                >
                  {collection.name}
                </Button>
              ))}
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-2 w-[350px] justify-between">
          <div className="grid grid-cols-2 min-w-[350px] text-md h-fit gap-2">
            <div className="text-fg-subtle font-semibold">Total {formatName("sale", true)}</div>
            <div className="font-semibold">
              {formatNumberWithSuffix(
                salesSum -
                  (!includeHistoricalPreorders ? knownHistoricalPreOrdersSum : 0) -
                  (!includeOngoingSales ? salesSum - knownHistoricalPreOrdersSum : 0),
                1,
              )}
            </div>
            <Separator className="col-span-2" />

            <div className="flex flex-row gap-2">
              <Switch checked={includeOngoingSales} onCheckedChange={setIncludeOngoingSales} />
              <div className="text-fg-subtle">Ongoing {formatName("sale", true)}</div>
            </div>
            <div className="font-medium">
              {formatNumberWithSuffix(salesSum - knownHistoricalPreOrdersSum, 1)}
            </div>
            <div className="flex flex-row gap-2">
              <Switch
                checked={includeHistoricalPreorders}
                onCheckedChange={setIncludeHistoricalPreorders}
              />
              <div className="text-fg-subtle">{formatName("preorder", true, true)}</div>
            </div>
            <div className="font-medium">
              {formatNumberWithSuffix(knownHistoricalPreOrdersSum, 1)}
            </div>
            <Separator className="col-span-2" />
          </div>
          <div className="grid grid-cols-2 min-w-[250px] text-md h-fit gap-2">
            <div className="text-fg-subtle font-semibold">Expected demand</div>
            <div className="font-semibold">
              {formatForecast(
                forecastSum -
                  (!includeKnownPreorders ? knownPreOrdersSum : 0) -
                  (!includeForecastedPreorders ? forecastedPreOrdersSum : 0) -
                  (!includeForecastWithoutPreorders ? forecastWithoutPreordersSum : 0),
              )}
            </div>
            <Separator className="col-span-2" />

            <div className="flex flex-row gap-2">
              <Switch
                checked={includeForecastWithoutPreorders}
                onCheckedChange={setIncludeForecastWithoutPreorders}
              />
              <div className="text-fg-subtle">Forecasted reorders</div>
            </div>
            <div className="font-medium">
              {formatNumberWithSuffix(forecastWithoutPreordersSum, 1)}
            </div>
            <div className="flex flex-row gap-2">
              <Switch checked={includeKnownPreorders} onCheckedChange={setIncludeKnownPreorders} />
              <div className="text-fg-subtle">Known {formatName("preorder", true)}</div>
            </div>
            <div className="font-medium">{formatNumberWithSuffix(knownPreOrdersSum, 1)}</div>
            <div className="flex flex-row gap-2">
              <Switch
                checked={includeForecastedPreorders}
                onCheckedChange={setIncludeForecastedPreorders}
              />
              <div className="text-fg-subtle">Forecasted {formatName("preorder", true)}</div>
            </div>
            <div className="font-medium">{formatForecast(forecastedPreOrdersSum)}</div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
